/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Store, User, Mail, Lock, ArrowLeft, ArrowRight,
  KeyRound, Eye, EyeOff, Loader2,
  PackageCheck, Truck, CreditCard, Handshake,
} from 'lucide-react';
import axios from 'axios';
import './VendorLoginModule.css';

/* ─── Password Input with toggle ─── */
function PwInput({ name, placeholder, onChange, icon: Icon = Lock }) {
  const [show, setShow] = useState(false);
  return (
    <div className="vnd-iw">
      <Icon size={18} className="vnd-iw-icon" />
      <input
        className="vnd-input"
        type={show ? 'text' : 'password'}
        name={name}
        placeholder={placeholder}
        onChange={onChange}
        required
        autoComplete="off"
      />
      <button
        type="button"
        className="vnd-pw-toggle"
        onClick={() => setShow(!show)}
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
      <span className="vnd-fline" />
    </div>
  );
}

/* ═══════════════════════════════════════════
   SUCCESS OVERLAY — rendered via portal to document.body
   so it always covers the FULL screen no matter where
   it is called from
   ═══════════════════════════════════════════ */
function SuccessOverlay({ onDone }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 1200);
    return () => clearTimeout(timer);
  }, [onDone]);

  return ReactDOM.createPortal(
    <div className="vnd-success-overlay">
      <div className="vnd-success-card">

        {/* animated checkmark ring */}
        <div className="vnd-success-ring">
          <svg className="vnd-success-svg" viewBox="0 0 52 52">
            <circle className="vnd-success-circle" cx="26" cy="26" r="23" />
            <path className="vnd-success-check" d="M14 27 l8 8 l16 -16" />
          </svg>
        </div>

        <p className="vnd-success-text">Welcome Back</p>
        <span className="vnd-success-sub">Redirecting to portal…</span>

        {/* progress bar */}
        <div className="vnd-success-bar">
          <div className="vnd-success-bar-fill" />
        </div>

      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
export default function VendorLoginModule({ onLoginSuccess, onBack }) {
  const [view, setView] = useState('login');
  const [animKey, setAnimKey] = useState(0);
  const leftRef = useRef(null);
  const partRef = useRef(null);

  const switchView = useCallback((v) => {
    setAnimKey((k) => k + 1);
    setView(v);
  }, []);

  /* ── Particles ── */
  useEffect(() => {
    const c = partRef.current;
    if (!c) return;
    const ps = [];
    for (let i = 0; i < 30; i++) {
      const p = document.createElement('div');
      p.className = 'vnd-pt';
      const sz = Math.random() * 2 + .8;
      p.style.cssText = `
        left:${Math.random() * 100}%;
        bottom:${Math.random() * -15}%;
        width:${sz}px;height:${sz}px;
        animation-duration:${Math.random() * 18 + 14}s;
        animation-delay:${Math.random() * 12}s;
      `;
      c.appendChild(p);
      ps.push(p);
    }
    return () => ps.forEach((p) => p.remove());
  }, []);

  /* ── Orb parallax ── */
  const onMouse = useCallback((e) => {
    const l = leftRef.current;
    if (!l) return;
    const r = l.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = (e.clientX - cx) / (r.width / 2);
    const dy = (e.clientY - cy) / (r.height / 2);
    l.querySelectorAll('.vnd-orb').forEach((o, i) => {
      const sp = (i + 1) * 12;
      o.style.transform = `translate(${dx * sp}px,${dy * sp}px)`;
    });
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouse);
    return () => window.removeEventListener('mousemove', onMouse);
  }, [onMouse]);

  const subtitles = {
    login: 'Partner with us. Please login to manage orders.',
    forgot: 'Reset your vendor account password securely.',
  };

  return (
    <div className="vnd-w">
      {/* ━━━ LEFT PANEL ━━━ */}
      <div className="vnd-left" ref={leftRef}>
        <div className="vnd-left-grid" />
        <div className="vnd-orb vnd-orb--1" />
        <div className="vnd-orb vnd-orb--2" />
        <div className="vnd-orb vnd-orb--3" />
        <div className="vnd-parts" ref={partRef} />
        <div className="vnd-shapes">
          <div className="vnd-sh vnd-sh--1" />
          <div className="vnd-sh vnd-sh--2" />
          <div className="vnd-sh vnd-sh--3" />
          <div className="vnd-sh vnd-sh--4" />
        </div>
        <div className="vnd-leaves">
          <div className="vnd-leaf vnd-leaf--1" />
          <div className="vnd-leaf vnd-leaf--2" />
          <div className="vnd-leaf vnd-leaf--3" />
        </div>

        <div className="vnd-left-content">
          <div className="vnd-left-brand">
            <div className="vnd-brand-box">
              <Store size={24} color="#fff" />
            </div>
            <span className="vnd-brand-name">TalentOza</span>
          </div>

          <h1 className="vnd-left-h">
            Partner<br />
            <span className="vnd-gr">Portal</span>
          </h1>

          <p className="vnd-left-p">
            Manage your business profile, submit bids, track
            purchase orders, and process invoices — all in one place.
          </p>

          <div className="vnd-left-feats">
            <div className="vnd-left-feat">
              <div className="vnd-feat-icon">
                <PackageCheck size={18} color="#34d399" />
              </div>
              <div className="vnd-feat-text">
                <h4>Order Fulfillment</h4>
                <p>Track and manage all your purchase orders seamlessly</p>
              </div>
            </div>
            <div className="vnd-left-feat">
              <div className="vnd-feat-icon">
                <Truck size={18} color="#5eead4" />
              </div>
              <div className="vnd-feat-text">
                <h4>Shipment Tracking</h4>
                <p>Real-time visibility into delivery status</p>
              </div>
            </div>
            <div className="vnd-left-feat">
              <div className="vnd-feat-icon">
                <CreditCard size={18} color="#a7f3d0" />
              </div>
              <div className="vnd-feat-text">
                <h4>Invoice Management</h4>
                <p>Submit, track, and reconcile invoices easily</p>
              </div>
            </div>
            <div className="vnd-left-feat">
              <div className="vnd-feat-icon">
                <Handshake size={18} color="#6ee7b7" />
              </div>
              <div className="vnd-feat-text">
                <h4>Partnership Hub</h4>
                <p>Collaborate with teams and manage contracts</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ━━━ RIGHT PANEL ━━━ */}
      <div className="vnd-right">
        {onBack && (
          <button className="vnd-back" onClick={onBack}>
            <ArrowLeft size={16} /> Back
          </button>
        )}

        <div className="vnd-form-card">
          {/* Header */}
          <div className="vnd-header">
            <div className="vnd-icon-wrap">
              <div className="vnd-icon-box">
                <Store size={30} color="var(--v-pri)" />
              </div>
              <div className="vnd-lock-badge">
                <Lock size={11} color="#fff" />
              </div>
            </div>
            <h1 className="vnd-h1">Partner Portal</h1>
            <p className="vnd-sub">{subtitles[view]}</p>
            <div className="vnd-sec-badge">
              <span className="vnd-sec-dot" />
              Secure Connection
            </div>
          </div>

          {/* Form views */}
          <div className="vnd-view" key={animKey}>
            {view === 'login' && (
              <LoginForm onSwitchView={switchView} onLoginSuccess={onLoginSuccess} />
            )}
            {view === 'forgot' && (
              <ForgotPasswordForm onSwitchView={switchView} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   LOGIN FORM
   ═══════════════════════════════════════════ */
function LoginForm({ onSwitchView, onLoginSuccess }) {
  const [formData, setFormData] = useState({ loginIdentifier: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/vendor/login`,
        formData
      );
      const vendorData = res.data.vendor;
      localStorage.setItem('vendorProfileData', JSON.stringify(vendorData));
      setIsLoading(false);
      setShowSuccess(true);
    } catch (err) {
      console.error('Login Error:', err);
      alert(err.response?.data?.message || 'Login Failed. Please check your credentials.');
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* portal renders to document.body — always fullscreen */}
      {showSuccess && (
        <SuccessOverlay
          onDone={() => {
            setShowSuccess(false);
            if (onLoginSuccess) onLoginSuccess();
          }}
        />
      )}

      <form className="vnd-form" onSubmit={handleLogin}>
        <div className="vnd-fg">
          <label className="vnd-label">Email or Username</label>
          <div className="vnd-iw">
            <User size={18} className="vnd-iw-icon" />
            <input
              className="vnd-input"
              type="text"
              name="loginIdentifier"
              onChange={handleChange}
              placeholder="Enter your email or username"
              required
              autoComplete="off"
            />
            <span className="vnd-fline" />
          </div>
        </div>

        <div className="vnd-fg">
          <label className="vnd-label">Password</label>
          <PwInput name="password" placeholder="Enter your password" onChange={handleChange} />
        </div>

        <div className="vnd-actions">
          <button type="button" className="vnd-link" onClick={() => onSwitchView('forgot')}>
            Forgot Password?
          </button>
        </div>

        <button
          type="submit"
          className={`vnd-btn ${isLoading ? 'vnd-btn--loading' : ''}`}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 size={20} className="vnd-spinner" />
          ) : (
            <>
              <span>Login</span>
              <ArrowRight size={18} />
            </>
          )}
        </button>

        <div className="vnd-divider">
          <span className="vnd-div-line" />
          <span className="vnd-div-text">Need Access?</span>
          <span className="vnd-div-line" />
        </div>

        <div className="vnd-info">
          Contact Admin to create an account
        </div>
      </form>
    </>
  );
}

/* ═══════════════════════════════════════════
   FORGOT PASSWORD FORM
   ═══════════════════════════════════════════ */
function ForgotPasswordForm({ onSwitchView }) {
  const [formData, setFormData] = useState({
    email: '', newPassword: '', confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/vendor/forgot-password`,
        {
          email: formData.email,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        }
      );
      alert('Password updated successfully. Please login.');
      onSwitchView('login');
    } catch (err) {
      console.error('Reset Error:', err);
      alert(err.response?.data?.message || 'Reset failed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="vnd-form" onSubmit={handleReset}>
      <div className="vnd-fg">
        <label className="vnd-label">Registered Email</label>
        <div className="vnd-iw">
          <Mail size={18} className="vnd-iw-icon" />
          <input
            className="vnd-input"
            type="email"
            name="email"
            onChange={handleChange}
            placeholder="Enter registered email"
            required
            autoComplete="off"
          />
          <span className="vnd-fline" />
        </div>
      </div>

      <div className="vnd-fg">
        <label className="vnd-label">New Password</label>
        <PwInput
          name="newPassword"
          placeholder="Enter new password"
          onChange={handleChange}
          icon={KeyRound}
        />
      </div>

      <div className="vnd-fg">
        <label className="vnd-label">Confirm New Password</label>
        <PwInput
          name="confirmPassword"
          placeholder="Confirm new password"
          onChange={handleChange}
          icon={KeyRound}
        />
      </div>

      <button
        type="submit"
        className={`vnd-btn ${isLoading ? 'vnd-btn--loading' : ''}`}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 size={20} className="vnd-spinner" />
        ) : (
          'Reset Password'
        )}
      </button>

      <button type="button" className="vnd-back-btn" onClick={() => onSwitchView('login')}>
        <ArrowLeft size={16} /> Back to Login
      </button>
    </form>
  );
}